import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Users, Trophy, ChevronRight } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useParams } from 'react-router-dom';
import { ttStatsAPI } from '../services/api';
import AnimatedText from './AnimatedText';
import { scrollToAppPromotion } from '../utils/scrollToApp';
import { PodiumColors } from '../theme/colors';

interface TopPlayer {
  name: string;
  club: string;
  eloRating: number;
  rank: number;
  licenseNumber: string;
}

interface TopClub {
  club: string;
  averageElo: number;
  playerCount: number;
  topPlayer: {
    name: string;
    eloRating: number;
  };
}

interface MostImproved {
  name: string;
  club: string;
  eloDelta: number;
  currentElo: number;
}

const MonthlyRankings: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const [activeTab, setActiveTab] = useState<'players' | 'clubs' | 'improved'>('players');
  const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([]);
  const [topClubs, setTopClubs] = useState<TopClub[]>([]);
  const [mostImproved, setMostImproved] = useState<MostImproved[]>([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    en: {
      title: 'Swiss Rankings',
      subtitle: 'Updated monthly • Official ELO ratings',
      topPlayers: 'Top Players',
      topClubs: 'Top Clubs',
      risingStars: 'Rising Stars',
      viewFullRankings: 'View full rankings in app',
      players: 'players',
      best: 'Best',
      avgElo: 'AVG ELO',
      elo: 'ELO',
      now: 'Now'
    },
    fr: {
      title: 'Classements Suisses',
      subtitle: 'Mis à jour chaque mois • Classifications ELO officielles',
      topPlayers: 'Top Joueurs',
      topClubs: 'Top Clubs',
      risingStars: 'Étoiles Montantes',
      viewFullRankings: 'Voir tous les classements dans l\'appli',
      players: 'joueurs',
      best: 'Meilleur',
      avgElo: 'ELO MOY',
      elo: 'ELO',
      now: 'Maintenant'
    },
    de: {
      title: 'Schweizer Ranglisten',
      subtitle: 'Monatlich aktualisiert • Offizielle ELO-Wertungen',
      topPlayers: 'Top Spieler',
      topClubs: 'Top Clubs',
      risingStars: 'Aufsteiger',
      viewFullRankings: 'Alle Rankings in der App ansehen',
      players: 'Spieler',
      best: 'Bester',
      avgElo: 'Ø ELO',
      elo: 'ELO',
      now: 'Jetzt'
    },
    it: {
      title: 'Classifiche Svizzere',
      subtitle: 'Aggiornate mensilmente • Classifiche ELO ufficiali',
      topPlayers: 'Top Giocatori',
      topClubs: 'Top Club',
      risingStars: 'Stelle Nascenti',
      viewFullRankings: 'Vedi tutte le classifiche nell\'app',
      players: 'giocatori',
      best: 'Migliore',
      avgElo: 'ELO MEDIO',
      elo: 'ELO',
      now: 'Ora'
    }
  };

  const t = translations[currentLang] || translations.en;

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const data = await ttStatsAPI.getMonthlyRankings();
        if (data) {
          setTopPlayers(data.topPlayers?.slice(0, 5) || []);
          setTopClubs(data.topClubs?.slice(0, 5) || []);
          setMostImproved(data.mostImproved?.slice(0, 5) || []);
        }
      } catch (error) {
        console.error('Failed to fetch rankings:', error);
        // Set empty arrays on error to prevent crashes
        setTopPlayers([]);
        setTopClubs([]);
        setMostImproved([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, []);

  const tabs = [
    { id: 'players', label: t.topPlayers, icon: Trophy },
    { id: 'clubs', label: t.topClubs, icon: Users },
    { id: 'improved', label: t.risingStars, icon: TrendingUp }
  ];

  return (
    <section className="py-16 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4">
        {/* Title */}
        <div className="text-center mb-10">
          {/* Lottie above title */}
          <motion.div
            className="flex justify-center mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div style={{ width: '240px', height: '240px' }}>
              <DotLottieReact
                src="/olympic_podium.lottie"
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </motion.div>
          <AnimatedText
            text={t.title}
            className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300"
            delay={0.1}
            staggerDelay={0.02}
          />
          <motion.p
            className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}>
            {t.subtitle}
          </motion.p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1 transition-colors duration-300">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                    ${activeTab === tab.id
                      ? 'text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                  `}>
                  <span className="relative z-10 flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </span>
                  {activeTab === tab.id && (
                    <motion.div
                      className="absolute inset-0 bg-white dark:bg-gray-700 rounded-full shadow-sm transition-colors duration-300"
                      layoutId="activeTab"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center py-12">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce transition-colors duration-300" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce transition-colors duration-300" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-bounce transition-colors duration-300" style={{ animationDelay: '300ms' }} />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl mx-auto">

              {/* Top Players */}
              {activeTab === 'players' && (
                <div className="space-y-2">
                  {topPlayers.map((player, index) => (
                    <motion.div
                      key={player.licenseNumber}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl transition-colors duration-300">
                      
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                          style={{
                            backgroundColor: index === 0 ? PodiumColors.first :
                                            index === 1 ? PodiumColors.second :
                                            index === 2 ? PodiumColors.third :
                                            '#e5e7eb',
                            color: index < 3 ? '#ffffff' : '#4b5563'
                          }}
                        >
                          {index + 1}
                        </div>
                        
                        <div>
                          <AnimatedText
                            text={player.name}
                            className="font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300"
                            delay={0.1 + index * 0.05}
                            staggerDelay={0.01}
                          />
                          <motion.p
                            className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 + index * 0.05 }}
                          >
                            {player.club}
                          </motion.p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{player.eloRating}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">{t.elo}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Top Clubs */}
              {activeTab === 'clubs' && (
                <div className="space-y-2">
                  {topClubs.map((club, index) => (
                    <motion.div
                      key={club.club}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl transition-colors duration-300">
                      
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                          style={{
                            backgroundColor: index === 0 ? PodiumColors.first :
                                            index === 1 ? PodiumColors.second :
                                            index === 2 ? PodiumColors.third :
                                            '#e5e7eb',
                            color: index < 3 ? '#ffffff' : '#4b5563'
                          }}
                        >
                          {index + 1}
                        </div>
                        
                        <div>
                          <AnimatedText
                            text={club.club}
                            className="font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300"
                            delay={0.1 + index * 0.05}
                            staggerDelay={0.01}
                          />
                          <motion.p
                            className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 + index * 0.05 }}
                          >
                            {club.playerCount} {t.players} • {t.best}: {club.topPlayer.name}
                          </motion.p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{Math.round(club.averageElo)}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">{t.avgElo}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Most Improved */}
              {activeTab === 'improved' && (
                <div className="space-y-2">
                  {mostImproved.map((player, index) => (
                    <motion.div
                      key={`${player.name}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl transition-colors duration-300">
                      
                      <div className="flex items-center gap-4">
                        {/* Trend Icon */}
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center transition-colors duration-300">
                          <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400 transition-colors duration-300" />
                        </div>

                        <div>
                          <AnimatedText
                            text={player.name}
                            className="font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300"
                            delay={0.1 + index * 0.05}
                            staggerDelay={0.01}
                          />
                          <motion.p
                            className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 + index * 0.05 }}
                          >
                            {player.club}
                          </motion.p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-semibold text-green-600 dark:text-green-400 transition-colors duration-300">+{player.eloDelta}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">{t.now}: {player.currentElo}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* View All Link */}
        {!loading && (
          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}>
            <button
              onClick={scrollToAppPromotion}
              className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300">
              {t.viewFullRankings}
              <ChevronRight className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default MonthlyRankings;